import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Bell, Send, Save, ExternalLink, ChevronDown, ChevronRight, Loader2, CheckCircle2, XCircle, Hash, Slack as SlackIcon,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAccounts } from "@/context/AccountContext";
import AccountPicker from "@/components/AccountPicker";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const CHANNELS = [
  { id: "telegram", label: "Telegram", icon: Bell,      color: "#3b82f6", testId: "tab-telegram" },
  { id: "discord",  label: "Discord",  icon: Hash,       color: "#a78bfa", testId: "tab-discord" },
  { id: "slack",    label: "Slack",    icon: SlackIcon,  color: "#10b981", testId: "tab-slack" },
];

const HELP_TEXT = {
  telegram: [
    "Open Telegram, search @BotFather, /newbot, follow prompts.",
    "Copy the token BotFather returns (format: 123456789:ABC…) into the field above.",
    "Send any message to your new bot so a chat exists.",
    "Search @userinfobot, send any message — it replies with your numeric Chat ID.",
    "Paste Chat ID above, click Test Message.",
  ],
  discord: [
    "In your Discord server: Settings → Integrations → Webhooks.",
    "Click New Webhook, pick a channel + name, click Copy Webhook URL.",
    "Paste the URL above and click Test Message.",
  ],
  slack: [
    "Visit https://api.slack.com/apps → Create App → From scratch.",
    "Enable Incoming Webhooks → Add New Webhook → pick a channel.",
    "Copy the webhook URL (starts with https://hooks.slack.com/…) and paste above.",
  ],
};

const AlertToggle = ({ label, description, checked, onChange, testId }) => (
  <div className="flex items-center justify-between py-3 border-b border-[#1f2937] last:border-0">
    <div>
      <div className="text-sm font-medium">{label}</div>
      {description && <div className="text-[11px] text-[#6b7280] mt-0.5">{description}</div>}
    </div>
    <Switch checked={checked} onCheckedChange={onChange} data-testid={testId} />
  </div>
);

const HelpSection = ({ channelId }) => {
  const [open, setOpen] = useState(false);
  const steps = HELP_TEXT[channelId] || [];
  const label = channelId === "telegram" ? "How do I get a Telegram Bot?"
    : channelId === "discord" ? "How do I get a Discord webhook URL?"
    : "How do I get a Slack webhook URL?";
  return (
    <div className="vx-card p-4" data-testid={`help-section-${channelId}`}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between text-left"
      >
        <div className="flex items-center gap-2">
          <ExternalLink className="w-3.5 h-3.5 text-[#a78bfa]" />
          <div className="vx-label">{label}</div>
        </div>
        {open ? <ChevronDown className="w-4 h-4 text-[#6b7280]" /> : <ChevronRight className="w-4 h-4 text-[#6b7280]" />}
      </button>
      {open && (
        <ol className="mt-3 pl-6 space-y-2 text-xs text-[#9ca3af] leading-relaxed list-decimal">
          {steps.map((s, i) => <li key={i}>{s}</li>)}
        </ol>
      )}
    </div>
  );
};

export default function Alerts() {
  const { activeAccount, activeAccountId } = useAccounts();
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null); // channel id being tested
  const [testResults, setTestResults] = useState({});
  const [touched, setTouched] = useState({ telegram: false, discord: false, slack: false });
  const [tab, setTab] = useState("telegram");

  const load = async () => {
    if (!activeAccountId) return;
    setLoading(true);
    try {
      const r = await api.get(`/alerts/config?account_id=${activeAccountId}`);
      setCfg(r.data);
      setTouched({ telegram: false, discord: false, slack: false });
      setTestResults({});
    } catch {
      toast.error("Failed to load alert config");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [activeAccountId]);

  if (!activeAccountId) {
    return <div className="text-[#9ca3af] font-mono text-sm">Select an account to configure alerts.</div>;
  }

  const ch = (id) => cfg?.[id] || {};
  const setCh = (id, patch) => setCfg((c) => ({ ...c, [id]: { ...(c[id] || {}), ...patch } }));
  const setAo = (id, patch) => setCfg((c) => ({
    ...c, [id]: { ...(c[id] || {}), alerts_on: { ...(c[id]?.alerts_on || {}), ...patch } },
  }));

  const buildAlertsOn = (ao) => ({
    new_signal: !!ao.new_signal,
    trade_opened: !!ao.trade_opened,
    trade_closed_win: !!ao.trade_closed_win,
    trade_closed_loss: !!ao.trade_closed_loss,
    daily_digest: !!ao.daily_digest,
    high_impact_news: !!ao.high_impact_news,
    challenge_passed: ao.challenge_passed !== false,
    challenge_at_risk: ao.challenge_at_risk !== false,
    challenge_50pct_progress: ao.challenge_50pct_progress !== false,
    min_confidence: Number(ao.min_confidence ?? 70),
  });

  const save = async () => {
    setSaving(true);
    try {
      const tg = ch("telegram");
      const dc = ch("discord");
      const sl = ch("slack");
      const payload = {
        account_id: activeAccountId,
        telegram: {
          enabled: !!tg.enabled,
          bot_token: touched.telegram ? (tg.bot_token || "") : (tg.bot_token || ""),
          chat_id: tg.chat_id || "",
          alerts_on: buildAlertsOn(tg.alerts_on || {}),
        },
        discord: {
          enabled: !!dc.enabled,
          webhook_url: touched.discord ? (dc.webhook_url || "") : (dc.webhook_url || ""),
          alerts_on: buildAlertsOn(dc.alerts_on || {}),
        },
        slack: {
          enabled: !!sl.enabled,
          webhook_url: touched.slack ? (sl.webhook_url || "") : (sl.webhook_url || ""),
          alerts_on: buildAlertsOn(sl.alerts_on || {}),
        },
      };
      const r = await api.put("/alerts/config", payload);
      setCfg(r.data);
      setTouched({ telegram: false, discord: false, slack: false });
      toast.success("Alert config saved across all channels");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const testChannel = async (channelId) => {
    setTesting(channelId);
    setTestResults((r) => ({ ...r, [channelId]: null }));
    try {
      const c = ch(channelId);
      const body = channelId === "telegram" ? {
        account_id: activeAccountId,
        bot_token: touched.telegram ? (c.bot_token || "") : "",
        chat_id: c.chat_id || "",
      } : {
        account_id: activeAccountId,
        webhook_url: touched[channelId] ? (c.webhook_url || "") : "",
      };
      const r = await api.post(`/alerts/test?account_id=${activeAccountId}&channel=${channelId}`, body);
      setTestResults((rr) => ({ ...rr, [channelId]: r.data }));
      if (r.data?.success) toast.success(`${channelId} test message delivered`);
      else toast.error(r.data?.error || "Test failed");
    } catch (e) {
      const err = e?.response?.data?.error || e?.response?.data?.detail || "Test failed";
      setTestResults((rr) => ({ ...rr, [channelId]: { success: false, error: err } }));
      toast.error(err);
    } finally { setTesting(null); }
  };

  const renderChannelCard = (channelId) => {
    const meta = CHANNELS.find((c) => c.id === channelId);
    const Icon = meta.icon;
    const c = ch(channelId);
    const ao = c.alerts_on || {};
    const testResult = testResults[channelId];
    const isTelegram = channelId === "telegram";
    return (
      <div className="space-y-4" data-testid={`channel-card-${channelId}`}>
        <div className="vx-card p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded flex items-center justify-center border" style={{ borderColor: `${meta.color}55`, background: `${meta.color}22` }}>
                <Icon className="w-4 h-4" style={{ color: meta.color }} />
              </div>
              <div>
                <div className="vx-label">{meta.label}</div>
                <div className="text-xs text-[#9ca3af] mt-0.5">
                  {isTelegram ? "Turn on once bot token & chat ID are validated" : "Turn on once the webhook URL is validated"}
                </div>
              </div>
            </div>
            <Switch
              checked={!!c.enabled}
              onCheckedChange={(v) => setCh(channelId, { enabled: v })}
              data-testid={`${channelId}-enabled-switch`}
            />
          </div>

          <div className="pt-2 border-t border-[#1f2937] space-y-3">
            {isTelegram ? (
              <>
                <div>
                  <label className="vx-label mb-1 block">Bot Token</label>
                  <Input
                    value={c.bot_token || ""}
                    onChange={(e) => { setCh("telegram", { bot_token: e.target.value }); setTouched((t) => ({ ...t, telegram: true })); }}
                    placeholder="123456789:ABC-DEF..."
                    data-testid="tg-bot-token"
                    className="bg-[#111827] border-[#1f2937] font-mono text-xs"
                  />
                  <div className="text-[10px] text-[#6b7280] mt-1 font-mono">
                    {touched.telegram ? "New token will be encrypted on save" : "Encrypted at rest — replace to update"}
                  </div>
                </div>
                <div>
                  <label className="vx-label mb-1 block">Chat ID</label>
                  <Input
                    value={c.chat_id || ""}
                    onChange={(e) => setCh("telegram", { chat_id: e.target.value })}
                    placeholder="e.g. 987654321"
                    data-testid="tg-chat-id"
                    className="bg-[#111827] border-[#1f2937] font-mono text-xs"
                  />
                </div>
              </>
            ) : (
              <div>
                <label className="vx-label mb-1 block">Webhook URL</label>
                <Input
                  value={c.webhook_url || ""}
                  onChange={(e) => { setCh(channelId, { webhook_url: e.target.value }); setTouched((t) => ({ ...t, [channelId]: true })); }}
                  placeholder={channelId === "discord" ? "https://discord.com/api/webhooks/…" : "https://hooks.slack.com/services/…"}
                  data-testid={`${channelId}-webhook-url`}
                  className="bg-[#111827] border-[#1f2937] font-mono text-xs"
                />
                <div className="text-[10px] text-[#6b7280] mt-1 font-mono">
                  {touched[channelId] ? "New URL will be encrypted on save" : "Encrypted at rest — replace to update"}
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between pt-2 border-t border-[#1f2937]">
            <button
              onClick={() => testChannel(channelId)}
              disabled={testing === channelId}
              data-testid={`${channelId}-test-btn`}
              className="h-9 px-4 rounded border text-xs font-mono flex items-center gap-1.5 disabled:opacity-50 hover:bg-[#1f2937]/50"
              style={{ borderColor: `${meta.color}66`, color: meta.color }}
            >
              {testing === channelId ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              {testing === channelId ? "Sending…" : "Test Message"}
            </button>
            {testResult && (
              <div
                className={`flex items-center gap-1.5 text-xs font-mono ${
                  testResult.success ? "text-[#10b981]" : "text-[#ef4444]"
                }`}
                data-testid={`${channelId}-test-result`}
              >
                {testResult.success ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
                {testResult.success ? "Message delivered!" : (testResult.error || "Failed")}
              </div>
            )}
          </div>
        </div>

        <div className="vx-card p-6 space-y-2" data-testid={`${channelId}-toggles-card`}>
          <div className="vx-label mb-2">Alerts to send via {meta.label}</div>
          <AlertToggle label="New signal (APPROVED)" description="Only auto-executable signals — filters ADVISORY / BLOCKED noise" checked={!!ao.new_signal} onChange={(v) => setAo(channelId, { new_signal: v })} testId={`${channelId}-new-signal`} />
          <AlertToggle label="Trade opened" checked={!!ao.trade_opened} onChange={(v) => setAo(channelId, { trade_opened: v })} testId={`${channelId}-trade-opened`} />
          <AlertToggle label="Trade closed — win" checked={!!ao.trade_closed_win} onChange={(v) => setAo(channelId, { trade_closed_win: v })} testId={`${channelId}-trade-closed-win`} />
          <AlertToggle label="Trade closed — loss" checked={!!ao.trade_closed_loss} onChange={(v) => setAo(channelId, { trade_closed_loss: v })} testId={`${channelId}-trade-closed-loss`} />
          <AlertToggle label="Daily digest" checked={!!ao.daily_digest} onChange={(v) => setAo(channelId, { daily_digest: v })} testId={`${channelId}-daily-digest`} />
          <AlertToggle label="High-impact news" checked={!!ao.high_impact_news} onChange={(v) => setAo(channelId, { high_impact_news: v })} testId={`${channelId}-high-impact-news`} />
          <AlertToggle label="Challenge pass / fail" checked={ao.challenge_passed !== false} onChange={(v) => setAo(channelId, { challenge_passed: v })} testId={`${channelId}-challenge-passed`} />
          <AlertToggle label="Challenge at risk" checked={ao.challenge_at_risk !== false} onChange={(v) => setAo(channelId, { challenge_at_risk: v })} testId={`${channelId}-challenge-at-risk`} />
          <AlertToggle label="Challenge 50% progress" checked={ao.challenge_50pct_progress !== false} onChange={(v) => setAo(channelId, { challenge_50pct_progress: v })} testId={`${channelId}-challenge-50pct`} />

          <div className="py-4 border-t border-[#1f2937]">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="text-sm font-medium">Minimum confidence for signal alerts</div>
                <div className="text-[11px] text-[#6b7280] mt-0.5">Filter out low-confidence noise</div>
              </div>
              <span className="font-mono text-sm w-12 text-right" style={{ color: meta.color }}>{ao.min_confidence ?? 70}</span>
            </div>
            <Slider
              value={[ao.min_confidence ?? 70]}
              min={0} max={100} step={5}
              onValueChange={(a) => setAo(channelId, { min_confidence: a[0] })}
              data-testid={`${channelId}-min-confidence-slider`}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="alerts-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="vx-label">Alerts</div>
          <h1 className="font-heading text-3xl md:text-4xl font-black tracking-tighter mt-1">
            Multi-Channel Alerts
          </h1>
          <p className="text-sm text-[#9ca3af] mt-2 max-w-2xl">
            Get real-time push notifications on Telegram, Discord, and Slack — enable one, two, or all three.
            Per-account config; a single Save button pushes everything at once.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="vx-label">Account:</span>
          <AccountPicker />
        </div>
      </div>

      {loading || !cfg ? (
        <div className="text-[#9ca3af] font-mono text-sm">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <div className="xl:col-span-2 space-y-4">
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="bg-[#0a0e1a] border border-[#1f2937]">
                {CHANNELS.map((cm) => {
                  const active = !!(cfg?.[cm.id]?.enabled);
                  return (
                    <TabsTrigger
                      key={cm.id}
                      value={cm.id}
                      data-testid={cm.testId}
                      className="text-xs font-mono flex items-center gap-1.5 data-[state=active]:bg-[#1f2937]/60"
                    >
                      <cm.icon className="w-3.5 h-3.5" style={{ color: cm.color }} />
                      {cm.label}
                      {active && <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#10b981" }} />}
                    </TabsTrigger>
                  );
                })}
              </TabsList>
              {CHANNELS.map((cm) => (
                <TabsContent key={cm.id} value={cm.id} className="mt-3">
                  {renderChannelCard(cm.id)}
                </TabsContent>
              ))}
            </Tabs>

            <div className="flex justify-end">
              <button
                onClick={save}
                disabled={saving}
                data-testid="save-alerts-btn"
                className="h-10 px-5 rounded bg-[#3b82f6] hover:bg-[#2563eb] transition-colors font-semibold text-sm flex items-center gap-2 disabled:opacity-60 text-white"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {saving ? "Saving…" : "Save All Channels"}
              </button>
            </div>
          </div>

          <div className="xl:col-span-1 space-y-4">
            <HelpSection channelId={tab} />
            <div className="vx-card p-4">
              <div className="vx-label mb-2">Enabled channels</div>
              <div className="space-y-2">
                {CHANNELS.map((cm) => {
                  const active = !!(cfg?.[cm.id]?.enabled);
                  return (
                    <div key={cm.id} className="flex items-center justify-between text-xs" data-testid={`enabled-summary-${cm.id}`}>
                      <div className="flex items-center gap-2">
                        <cm.icon className="w-3.5 h-3.5" style={{ color: cm.color }} />
                        <span>{cm.label}</span>
                      </div>
                      <span className={`font-mono text-[10px] uppercase ${active ? "text-[#10b981]" : "text-[#6b7280]"}`}>
                        {active ? "ON" : "OFF"}
                      </span>
                    </div>
                  );
                })}
              </div>
              <div className="mt-4 pt-3 border-t border-[#1f2937] text-[10px] text-[#6b7280] leading-relaxed">
                All secrets are encrypted at rest (Fernet). Test messages hit each service&apos;s live API — a friendly error appears here if the credentials are wrong.
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
